import React, { MouseEvent, useState } from "react";
import { Box, Button, Typography, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Version } from "../../../types/version";
import { useReferenceVersionConnectionController } from "../../../controllers/connections/ReferenceVersionConnectionController";
import ReferenceVersionTable from "../../../components/tables/ReferenceVersionTable";
import {
  SystemVersionConnectionDirection,
  SystemVersionReferenceToEntity,
} from "../../../types/referenceVersion";

interface ReferenceVersionSectionProps {
  anchor: Version;
  systemLanguageType: Version["system_language_type"];
}
interface MenuOption {
  label: string;
  referenceToEntity: SystemVersionReferenceToEntity;
  connectionDirection: SystemVersionConnectionDirection;
}

const menuOptions: MenuOption[] = [
  {
    label: "Refer to Person",
    referenceToEntity: SystemVersionReferenceToEntity.PERSON,
    connectionDirection: SystemVersionConnectionDirection.REFERRING_TO,
  },
  {
    label: "Refer to Version",
    referenceToEntity: SystemVersionReferenceToEntity.VERSION,
    connectionDirection: SystemVersionConnectionDirection.REFERRING_TO,
  },
  {
    label: "Referenced by Version",
    referenceToEntity: SystemVersionReferenceToEntity.VERSION,
    connectionDirection: SystemVersionConnectionDirection.REFERENCED_BY,
  },
];

const ReferenceVersionSection: React.FC<ReferenceVersionSectionProps> = ({
  anchor,
  systemLanguageType,
}: ReferenceVersionSectionProps) => {
  const [selectedOption, setSelectedOption] = useState<MenuOption>(
    menuOptions[0]
  );

  const controller = useReferenceVersionConnectionController({
    from: anchor,
    referenceToEntity: selectedOption.referenceToEntity,
    connectionDirection: selectedOption.connectionDirection,
  });

  const { openCreateDialog } = controller;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleSelect = (option: MenuOption) => {
    setSelectedOption(option);
    closeMenu();
    openCreateDialog();
  };

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      <Box sx={{ position: "relative", mb: 2, textAlign: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openMenu}
          sx={{ position: "absolute", left: 16 }}
        >
          New Version Reference
        </Button>
        <Typography variant="h2">Reference Versions</Typography>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          {menuOptions.map((option) => (
            <MenuItem key={option.label} onClick={() => handleSelect(option)}>
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <ReferenceVersionTable
        controller={controller}
        systemLanguageType={systemLanguageType}
      />
    </Box>
  );
};

export default ReferenceVersionSection;
